import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { useCore } from "../lib/core";

interface HelpControls {
  /** Opens the contextual panel for the screen currently on show. */
  open: () => void;
  /** True while the panel is on screen. */
  isOpen: boolean;
}

const HelpContext = createContext<HelpControls>({ open: () => {}, isOpen: false });
const HelpAnchorContext = createContext<(id: string | null) => void>(() => {});

export function useHelp(): HelpControls {
  return useContext(HelpContext);
}

/**
 * Lets a screen say which article the `?` should open, when the URL cannot.
 *
 * Settings is the case that forced this: it has one route and keeps the open
 * panel in local state, so without a declared anchor the owner reading the
 * organisation settings always got the personal-preferences article.
 */
export function useHelpAnchor(id: string | null): void {
  const set = useContext(HelpAnchorContext);
  useEffect(() => {
    set(id);
    return () => set(null);
  }, [set, id]);
}

/**
 * Hosts the contextual guide panel for the whole authenticated shell.
 *
 * `@praesto/help` — catalogue, components and every article — is imported only
 * when the reader first asks for help, so the guide costs nothing on boot. The
 * import sits behind state rather than `React.lazy` because the app has no
 * `<Suspense>` boundary anywhere, and the first article opened is not the place
 * to discover that.
 */
export function HelpProvider({ children }: { children: ReactNode }) {
  const [isOpen, setOpen] = useState(false);
  const [mod, setMod] = useState<typeof import("@praesto/help") | null>(null);
  const [failed, setFailed] = useState(false);
  const core = useCore();
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug?: string };

  useEffect(() => {
    if (!isOpen || mod || failed) return;
    let cancelled = false;
    void import("@praesto/help")
      .then((m) => {
        if (!cancelled) setMod(m);
      })
      .catch(() => {
        // Offline, or a stale index.html pointing at a chunk the last deploy
        // removed. Without this the panel simply never appeared: `isOpen`
        // stayed true, the effect never re-ran, and the `?` was dead for the
        // rest of the session with only an unhandled rejection to show for it.
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, mod, failed]);

  const [declaredAnchor, setDeclaredAnchor] = useState<string | null>(null);
  const open = useCallback(() => setOpen(true), []);
  const controls = useMemo(() => ({ open, isOpen }), [open, isOpen]);

  const articles = useMemo(() => {
    if (!mod) return [];
    return mod.visibleArticles(mod.catalog, {
      isModuleActive: (id) => core.tenant.isModuleActive(id),
      can: (action, resource) => core.rbac.can(action, resource),
    });
  }, [mod, core]);

  const Drawer = mod?.HelpDrawer as ComponentType<Record<string, unknown>> | undefined;

  return (
    <HelpContext.Provider value={controls}>
      <HelpAnchorContext.Provider value={setDeclaredAnchor}>{children}</HelpAnchorContext.Provider>
      {isOpen && !mod && (
        <HelpFallback
          message={core.i18n.t(failed ? "help.loadFailed" : "help.loading")}
          retry={failed ? () => setFailed(false) : undefined}
          dismiss={() => {
            setFailed(false);
            setOpen(false);
          }}
          dismissLabel={core.i18n.t("shell.action.close")}
          retryLabel={core.i18n.t("help.retry")}
        />
      )}
      {/* Mounted as soon as the package is loaded, not only while open: the
          Drawer's enter/exit animation needs to SEE `open` go false, and
          unmounting on close made the guide vanish with a snap while every
          other drawer in the app fades out. */}
      {mod && Drawer && (
        <Drawer
          open={isOpen}
          onClose={() => setOpen(false)}
          articles={articles}
          pathname={location.pathname}
          namedAnchor={declaredAnchor ?? namedAnchorFor(location.pathname, core)}
          t={(key: string) => core.i18n.t(key)}
          onOpenFull={(articleId?: string) => {
            setOpen(false);
            // `search` as an object, not glued onto `to`: TanStack treats `to`
            // as a pathname and appends the current search after it, so a
            // hand-written "?a=x" became "?a=x?q=y" whenever the page already
            // had a query string.
            void navigate({
              to: `/${tenantSlug}/help` as never,
              search: (articleId ? { a: articleId } : {}) as never,
            });
          }}
        />
      )}
    </HelpContext.Provider>
  );
}

/**
 * Shown while the guide package is loading, and when it failed to load.
 *
 * Deliberately NOT the ui-kit Drawer: that lives inside the very chunk that did
 * not arrive. A plain sheet keeps the failure path free of the dependency that
 * caused it.
 */
function HelpFallback({
  message,
  retry,
  dismiss,
  dismissLabel,
  retryLabel,
}: {
  message: string;
  retry?: (() => void) | undefined;
  dismiss: () => void;
  dismissLabel: string;
  retryLabel: string;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-end justify-center bg-overlay sm:items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) dismiss();
      }}
    >
      <div className="w-full rounded-t-lg bg-elevated p-4 shadow-md sm:max-w-sm sm:rounded-lg">
        <p className="text-sm text-fg-default">{message}</p>
        <div className="mt-4 flex justify-end gap-2">
          {retry && (
            <button
              type="button"
              onClick={retry}
              className="rounded-md px-3 py-1.5 text-sm min-h-touch hover:bg-hover"
            >
              {retryLabel}
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            className="rounded-md px-3 py-1.5 text-sm text-fg-muted min-h-touch hover:bg-hover"
          >
            {dismissLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Some screens cannot be told apart by their URL. `/shifts/week` renders the
 * planning grid for whoever may publish and the personal shift list for
 * everyone else, so the route alone would hand half the readers the wrong
 * article.
 */
function namedAnchorFor(pathname: string, core: ReturnType<typeof useCore>): string | undefined {
  const [, , moduleId, sub] = pathname.split("/");
  if (moduleId === "shifts" && (sub === "week" || sub === undefined)) {
    return core.rbac.can("publish", "shift") ? "shifts.grid" : "shifts.mine";
  }
  return undefined;
}
